if (!customElements.get('product-recommendation-feed')) {
  customElements.define('product-recommendation-feed', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.grid = this.querySelector('[data-feed-grid]');
      this.button = this.querySelector('[data-feed-more]');
      this.status = this.querySelector('[data-feed-status]');
      if (!this.grid || !this.button) return;
      this.ready = true;
      this.cats = ['fashion-bags','fashion-phone-case','modern-style-earrings-collection','fashion-sunglasses','fashion-watches','modern-style-bracelet-collection','fashion-scarf','trendy-unisex-fashion-baseball-cap','modern-style-necklace-collection','fashion-shoes','fragrance','modern-style-rings-collection','fashion-sandals-slippers'];
      this.plan = [...this.cats, ...this.cats.filter(c => !['fragrance','fashion-sandals-slippers'].includes(c))];
      this.pools = new Map();
      this.seen = new Set([this.dataset.currentProductId]);
      this.cartIds = new Set((this.dataset.cartIds || '').split(','));
      this.pending = [];
      this.planIndex = 0;
      this.observer = new ResizeObserver(() => this.layout());
      this.grid.classList.add('product-feed__grid--masonry');
      this.button.addEventListener('click', () => this.loadMore());
      this.loadMore();
    }
    disconnectedCallback() { this.observer?.disconnect(); }
    layout() {
      const gap = parseFloat(getComputedStyle(this).getPropertyValue('--feed-row-gap')) || 30;
      for (const card of this.grid.children) card.style.gridRowEnd = `span ${Math.max(1, Math.ceil(card.getBoundingClientRect().height + gap))}`;
    }
    root() { return (this.dataset.root || '/').replace(/\/?$/, '/'); }
    pool(handle) {
      if (!this.pools.has(handle)) {
        const url = new URL(`${this.root()}collections/${handle}`, location.origin);
        url.searchParams.set('section_id', 'product-discovery-pool');
        url.searchParams.set('sort_by', 'best-selling');
        this.pools.set(handle, { queue: [], next: url.href, done: false, visited: new Set() });
      }
      return this.pools.get(handle);
    }
    async request(url) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('Request failed');
        return await response.text();
      } finally { clearTimeout(timer); }
    }
    async fill(handle) {
      const pool = this.pool(handle);
      if (pool.done) return;
      const url = new URL(pool.next, location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid pagination origin');
      url.searchParams.set('section_id', 'product-discovery-pool');
      url.searchParams.set('sort_by', 'best-selling');
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      if (pool.visited.has(url.href)) throw new Error('Pagination did not advance');
      const doc = new DOMParser().parseFromString(await this.request(url), 'text/html');
      const box = doc.querySelector('[data-ld-pool]');
      if (!box) throw new Error('Missing product pool');
      pool.visited.add(url.href);
      pool.queue.push(...box.querySelectorAll('[data-product-id]'));
      pool.next = box.dataset.next ? new URL(box.dataset.next, url).href : '';
      pool.done = !pool.next;
    }
    usable(node) { return !this.seen.has(node.dataset.productId) && !this.cartIds.has(node.dataset.productId); }
    async take(handle) {
      const pool = this.pool(handle);
      while (true) {
        while (pool.queue.length) {
          const node = pool.queue.shift();
          if (this.usable(node)) {
            this.seen.add(node.dataset.productId);
            node.dataset.pool = handle;
            return node;
          }
        }
        if (pool.done) return null;
        await this.fill(handle);
      }
    }
    async refreshCart() {
      const cart = JSON.parse(await this.request(new URL(`${this.root()}cart.js`, location.origin)));
      this.cartIds = new Set(cart.items.map(item => String(item.product_id)));
      this.pending = this.pending.filter(node => !this.cartIds.has(node.dataset.productId));
      for (const card of [...this.grid.children]) if (this.cartIds.has(card.dataset.productId)) card.remove();
    }
    async loadMore() {
      if (this.loading || this.exhausted) return;
      this.loading = true;
      this.button.disabled = true;
      this.button.textContent = this.dataset.loading;
      this.status.textContent = '';
      this.setAttribute('aria-busy', 'true');
      try {
        await this.refreshCart();
        const fills = await Promise.allSettled(this.cats.map(handle => {
          const pool = this.pool(handle);
          return pool.queue.length || pool.done ? Promise.resolve() : this.fill(handle);
        }));
        if (fills.some(result => result.status === 'rejected')) throw new Error('Product pool unavailable');
        while (this.planIndex < this.plan.length && this.pending.length < 24) {
          const node = await this.take(this.plan[this.planIndex]);
          this.planIndex++;
          if (node) this.pending.push(node);
        }
        while (this.pending.length < 24) {
          let added = false;
          for (const handle of this.cats) {
            if (this.pending.length === 24) break;
            const node = await this.take(handle);
            if (node) { this.pending.push(node); added = true; }
          }
          if (!added) break;
        }
        const batch = this.pending.splice(0, 24);
        this.planIndex = 0;
        const fragment = document.createDocumentFragment();
        batch.forEach(node => fragment.append(node));
        this.grid.append(fragment);
        batch.forEach(node => this.observer.observe(node));
        this.layout();
        this.exhausted = this.cats.every(handle => {
          const pool = this.pool(handle);
          return pool.done && !pool.queue.some(node => this.usable(node));
        });
        this.button.hidden = this.exhausted;
        this.status.textContent = this.exhausted ? this.dataset.end : '';
        this.dispatchEvent(new CustomEvent('product-feed:appended', { bubbles: true, detail: { count: batch.length } }));
      } catch (error) {
        this.button.hidden = false;
        this.status.textContent = this.dataset.error;
      } finally {
        this.loading = false;
        this.button.disabled = false;
        this.button.textContent = this.dataset.more;
        this.removeAttribute('aria-busy');
      }
    }
  });
}
